import { useEffect, useMemo, useReducer, useState } from "react";
import { Checkbox, Popover } from "@mui/material";
import { createUseStyles } from "react-jss";

import { Button, Text } from "./index";
import BataillogDatePicker from "./BataillogDatePicker/BataillogDatePicker";
import { theme } from "$utils/theme/bataillog";

type StatusHistoryItem = {
  date: Date | string;
  value: any;
  label: string;
  checked?: boolean;
  locked?: boolean;
  nullable?: boolean;
  isAlwaysEditable?: boolean;
};

type Props = {
  disabled?: boolean;
  currentStatus: any;
  statusHistory: StatusHistoryItem[];
  hiddenValues?: any[];
  lockedValues?: any[];
  nullableValues?: any[];
  alwaysEditableValues?: any[];
  /** Min date per status (e.g. TO_SHIP >= max OP validation date). When set, the date picker is enabled for that status even when first row. */
  minDateByStatus?: Record<string, Date | string | null>;
  updateStatus: (
    selectedStatus: any,
    statusHistory: Pick<StatusHistoryItem, "date" | "value">[]
  ) => Promise<void> | null;
  isFetching: boolean;
};

type SetStatusHistoryAction = {
  type: "setStatusHistory";
  statusHistory: StatusHistoryItem[];
};

type ToggleCheckBoxAction = {
  type: "toggleCheckbox";
  index: number;
};

type ChangeDateAction = {
  type: "changeDate";
  index: number;
  date: Date;
};

type ReducerAction =
  | SetStatusHistoryAction
  | ToggleCheckBoxAction
  | ChangeDateAction;

function statusHistoryReducer(
  state: StatusHistoryItem[],
  action: ReducerAction
) {
  switch (action.type) {
    case "setStatusHistory":
      return [...action.statusHistory];
    case "toggleCheckbox":
      return state.map((item, index) => {
        const checkboxIndex = action.index;
        if (index === checkboxIndex) {
          return {
            ...item,
            checked: !item.checked,
            date: item.checked ? null : item.date,
          };
        }
        return item;
      });
    case "changeDate":
      return state.map((item, index) => {
        if (index === action.index) {
          return {
            ...item,
            date: action.date,
          };
        }
        return item;
      });
    default:
      return state;
  }
}

const isCheckboxDisabled = (
  statusHistoryItem: StatusHistoryItem,
  index: number,
  items: StatusHistoryItem[]
) => {
  if (statusHistoryItem.isAlwaysEditable) return false;
  return (
    statusHistoryItem.locked || // si le status est verrouillé
    index === 0 || // si c'est le premier status
    (items[index - 1] &&
      ((!items[index - 1].date && !items[index - 1].nullable) ||
        items[index - 1].locked)) || // si le status précédent n'a pas de date et n'est pas nullable ou est verrouillé
    (items[index + 1] && items[index + 1].checked && !items[index + 1].locked) // si le status suivant est coché et n'est pas verrouillé
  );
};

const isDatePickerDisabled = (
  statusHistoryItem: StatusHistoryItem,
  index: number,
  items: StatusHistoryItem[],
  minDateByStatus?: Record<string, Date | string | null>
) => {
  if (statusHistoryItem.locked) return true;
  const hasMinDateOverride =
    minDateByStatus && statusHistoryItem.value in minDateByStatus;
  // Pour les statuts avec minDateByStatus (ex. A expédier), on garde le date picker actif même en première ligne / non coché
  if (hasMinDateOverride) {
    return !!(
      items[index + 1] &&
      items[index + 1].checked &&
      !items[index + 1].locked
    );
  }
  return (
    index === 0 ||
    !statusHistoryItem.checked ||
    !!(items[index + 1] && items[index + 1].checked && !items[index + 1].locked)
  );
};

function GenericStatusHistoryButton({
  disabled = false,
  statusHistory,
  lockedValues,
  nullableValues,
  hiddenValues,
  alwaysEditableValues,
  minDateByStatus,
  updateStatus,
  currentStatus,
  isFetching,
}: Props) {
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement | null>(
    null
  );
  const [statusHistoryItems, dispatch] = useReducer(statusHistoryReducer, []);

  const currentStatusLabel = useMemo(
    () => statusHistory.find((status) => status.value === currentStatus)?.label,
    [currentStatus, statusHistory]
  );

  const selectedStatus = useMemo(
    () =>
      statusHistoryItems.findLast((status) => status.checked && !status.locked)
        ?.value || currentStatus,
    [statusHistoryItems, currentStatus]
  );

  const isFormValid = useMemo(
    () =>
      statusHistoryItems.find((item) => item.value === selectedStatus)?.date ||
      nullableValues?.includes(selectedStatus),
    [statusHistoryItems, selectedStatus, nullableValues]
  );

  const isFormLocked = lockedValues?.length >= statusHistory.length;

  useEffect(() => {
    if (!popoverAnchor) return;
    const possibleValues = statusHistory.filter(
      (status) => !hiddenValues?.includes(status.value)
    );
    const currentStatusIndex = possibleValues.findIndex(
      (item) => item.value === currentStatus
    );
    const statusHistoryItems = possibleValues.map(
      (statusHistoryItem, index) => {
        const locked = lockedValues?.includes(statusHistoryItem.value);
        const checked = locked || index <= currentStatusIndex || index === 0;
        const isAlwaysEditable =
          !locked && alwaysEditableValues?.includes(statusHistoryItem.value);
        return {
          ...statusHistoryItem,
          locked,
          checked,
          nullable: nullableValues?.includes(statusHistoryItem.value),
          isAlwaysEditable,
        };
      }
    );
    dispatch({ type: "setStatusHistory", statusHistory: statusHistoryItems });
  }, [
    currentStatus,
    statusHistory,
    dispatch,
    hiddenValues,
    lockedValues,
    nullableValues,
    alwaysEditableValues,
    popoverAnchor,
  ]);

  const classes = useStyles();

  const handleClosePopover = () => {
    setPopoverAnchor(null);
  };

  const handleUpdate = () => {
    updateStatus(
      selectedStatus,
      statusHistoryItems.map((item) => ({
        // Convert Date objects to ISO strings to ensure consistent format
        // The date picker returns Date objects, but the API expects ISO strings
        date: item.date instanceof Date ? item.date.toISOString() : item.date,
        value: item.value,
      }))
    );
    setPopoverAnchor(null);
  };

  const handleOpenPopover = (e: any) => {
    setPopoverAnchor(e.currentTarget);
  };

  const minDatesForRows = useMemo(() => {
    return statusHistoryItems.map((statusHistoryItem, index) => {
      // Base min date should be the latest date among previous statuses (not the last date overall),
      // otherwise TO_SHIP would be constrained by its own current date.
      let base: Date | undefined;
      for (let i = index - 1; i >= 0; i -= 1) {
        const d = statusHistoryItems[i]?.date;
        if (d) {
          base = new Date(d);
          break;
        }
      }

      const override =
        minDateByStatus?.[statusHistoryItem.value] ??
        (String(statusHistoryItem.value) === "TO_SHIP"
          ? minDateByStatus?.TO_SHIP
          : undefined);
      if (override != null && override !== "") {
        const d = new Date(override);
        return base ? new Date(Math.max(base.getTime(), d.getTime())) : d;
      }
      return base ?? undefined;
    });
  }, [statusHistoryItems, minDateByStatus]);

  const renderPopOver = () => {
    return statusHistoryItems.map((statusHistoryItem, index, items) => (
      <div
        key={statusHistoryItem.value}
        className={classes.line}
        style={{ justifyContent: "space-between", margin: 15 }}
      >
        <div style={{ paddingRight: 15 }}>
          {!statusHistoryItem.locked && (
            <Checkbox
              checked={statusHistoryItem.checked}
              disabled={isCheckboxDisabled(statusHistoryItem, index, items)}
              value={statusHistoryItem.value}
              onChange={() => {
                dispatch({ type: "toggleCheckbox", index });
              }}
            />
          )}
          <Text
            style={{
              fontWeight:
                statusHistoryItem.value === selectedStatus ? "bold" : "normal",
              marginLeft: statusHistoryItem.locked ? 40 : 0,
            }}
          >
            {statusHistoryItem.label}
          </Text>
        </div>
        <div className={classes.inputContainer}>
          <BataillogDatePicker
            selectedDate={
              statusHistoryItem.date ? new Date(statusHistoryItem.date) : null
            }
            onDateChange={(dates) => {
              if (!Array.isArray(dates)) {
                dispatch({ type: "changeDate", index, date: dates });
              }
            }}
            minDate={minDatesForRows[index] ?? undefined}
            disabled={isDatePickerDisabled(
              statusHistoryItem,
              index,
              items,
              minDateByStatus
            )}
            isClearable={
              !isDatePickerDisabled(
                statusHistoryItem,
                index,
                items,
                minDateByStatus
              )
            }
          />
        </div>
      </div>
    ));
  };

  return (
    <div>
      <Button
        disabled={disabled}
        variant="contained"
        onClick={handleOpenPopover}
      >
        {currentStatusLabel}
      </Button>
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <div>
          {renderPopOver()}
          <div className={classes.buttonBar}>
            <Button variant="outlined" onClick={handleClosePopover}>
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdate}
              disabled={isFetching || !isFormValid || disabled || isFormLocked}
            >
              Enregistrer
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
}

const useStyles = createUseStyles(() => ({
  line: {
    display: "flex",
    alignItems: "center",
  },
  buttonBar: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
    justifyContent: "center",
    marginBottom: theme.spacing(1),
  },
  inputContainer: {
    "& input::-webkit-calendar-picker-indicator": {
      display: "block",
      "-webkit-appearance": "textfield",
    },
    width: 200,
  },
  input: {
    paddingTop: `${theme.spacing(1)}px !important`,
    paddingBottom: `${theme.spacing(1)}px !important`,
  },
  inputDate: {
    width: "162px !important",
  },
}));

export default GenericStatusHistoryButton;
